import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Container,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Box,
  Grid,
} from "@mui/material";
import toast from "react-hot-toast";
import { getDeliveriesByCustomerId } from "../../services/deliveryService";

const DeliveryHistory = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { customer } = location.state || {};

  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPaid, setTotalPaid] = useState(0);
  const [totalDue, setTotalDue] = useState(0);

  useEffect(() => {
    if (!customer) {
      toast.error("No customer data found.");
      navigate("/");
      return;
    }
    fetchDeliveryHistory();
  }, [customer]);

  const fetchDeliveryHistory = async () => {
    setLoading(true);
    try {
      const response = await getDeliveriesByCustomerId(customer.id);

      console.log("🚀 API Response:", response); // ✅ Debugging API response

      if (response && response.length > 0) {
        setDeliveries(response);

        const totalPaidAmount = response.reduce(
          (sum, delivery) => sum + delivery.paid_amount,
          0
        );
        const totalDueAmount = response.reduce(
          (sum, delivery) => sum + delivery.due_amount,
          0
        );

        setTotalPaid(totalPaidAmount);
        setTotalDue(totalDueAmount);
      } else {
        setDeliveries([]);
        toast.info("No deliveries found for this customer.");
      }
    } catch (error) {
      console.error("Failed to fetch delivery history:", error);
      toast.error("Failed to fetch delivery history. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!deliveries.length) {
    return (
      <Typography align="center" sx={{ mt: 4 }}>
        No deliveries found for this customer.
      </Typography>
    );
  }

  return (
    <Container maxWidth="xs" sx={{ py: 2 }}>
      <Typography
        variant="h6"
        align="center"
        gutterBottom
        sx={{ fontWeight: "bold", color: "#374151" }}
      >
        Delivery History for {customer.name}
      </Typography>

      {deliveries.map((delivery, index) => (
        <Card
          key={index} // Use `index` as key if `delivery_id` is undefined
          variant="outlined"
          sx={{
            mb: 1.5,
            p: 1.5,
            borderRadius: "8px",
            backgroundColor: "#F9FAFB",
          }}
        >
          <CardContent sx={{ p: 0 }}>
            <Grid container spacing={1}>
              {/* Product Name */}
              <Grid item xs={12}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: "bold", color: "#1F2937" }}
                >
                  {delivery?.product?.product_name || "Unknown Product"}
                </Typography>
              </Grid>

              {/* Date */}
              <Grid item xs={6}>
                <Typography
                  variant="caption"
                  sx={{ color: "#4B5563", fontSize: "0.75rem" }}
                >
                  Date:{" "}
                  {delivery.delivery_date
                    ? new Date(delivery.delivery_date).toLocaleDateString()
                    : "N/A"}
                </Typography>
              </Grid>

              {/* Quantity */}
              <Grid item xs={6}>
                <Typography
                  variant="caption"
                  sx={{ color: "#4B5563", fontSize: "0.75rem" }}
                >
                  Quantity: {delivery.quantity_delivered || "0"}
                </Typography>
              </Grid>

              {/* Paid Amount */}
              <Grid item xs={6}>
                <Typography
                  variant="caption"
                  sx={{ color: "#15803D", fontSize: "0.75rem" }}
                >
                  Paid: ₹{delivery.paid_amount || "0"}
                </Typography>
              </Grid>

              {/* Due Amount */}
              <Grid item xs={6}>
                <Typography
                  variant="caption"
                  sx={{ color: "#DC2626", fontSize: "0.75rem" }}
                >
                  Due: ₹{delivery.due_amount || "0"}
                </Typography>
              </Grid>

              {/* Cool & Pet Bottle Returns */}
              <Grid item xs={12}>
                <Typography
                  variant="caption"
                  sx={{ color: "#4B5563", fontSize: "0.75rem" }}
                >
                  {/* ✅ Ensure `product_id` exists before using it */}
                  {delivery.product_id ? (
                    <>
                      {delivery.product_id === 1 && (
                        <>
                          🛢️ Cool Returned:{" "}
                          <strong>{delivery.cool_bottles_returned || "0"}</strong>
                        </>
                      )}
                      {delivery.product_id === 2 && (
                        <>
                          🍼 Pet Returned:{" "}
                          <strong>{delivery.pet_bottles_returned || "0"}</strong>
                        </>
                      )}
                    </>
                  ) : (
                    "N/A"
                  )}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ))}
    </Container>
  );
};

export default DeliveryHistory;
